import { useState } from "react";
import { StaffFormData } from "../types/staff";
import api from "../../../../../features/auth/axiosConfig";
import { notifyError, notifySuccess } from "src/assets/toastService";

export const useStaffOperations = (refetch: () => void) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // --------------------------
  // Build correct FormData
  // --------------------------
  const buildFormData = (formData: StaffFormData) => {
    const fd = new FormData();
    fd.append("staff_name", formData.staff_name);
    fd.append("phone_number", formData.phone_number);

    if (formData.image instanceof File) {
      fd.append("image", formData.image);
    }

    return fd;
  };

  // --------------------------
  // ADD STAFF
  // --------------------------
  const addStaff = async (formData: StaffFormData) => {
    setIsProcessing(true);
    try {
      const apiFormData = buildFormData(formData);

      const res = await api.post("/app/v1/pos/staff", apiFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Show success toast ONLY here
      notifySuccess("Staff added successfully!");

      refetch();
      return true;
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 409) {
        notifyError(message);
        return false;
      }
      notifyError("Failed to add staff.");
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  // --------------------------
  // UPDATE STAFF
  // --------------------------
  const updateStaff = async (id: string, formData: StaffFormData) => {
    setIsProcessing(true);
    try {
      const apiFormData = buildFormData(formData);

      const res = await api.put(`/app/v1/pos/staff/${id}`, apiFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Show success toast ONLY here
      notifySuccess("Staff updated successfully!");

      refetch();
      return true;
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;

      if (status === 409) {
        notifyError(message);
        return false;
      }

      notifyError("Failed to update staff.");
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  // --------------------------
  // DELETE STAFF
  // --------------------------
  const deleteStaff = async (id: string) => {
    setIsProcessing(true);
    try {
      const res = await api.delete(`/app/v1/pos/staff/${id}`);

      // Show success toast ONLY here
      notifySuccess("Staff deleted successfully!");

      refetch();
      return true;
    } catch (error: any) {
      // Check for specific error messages if needed
      const message = error?.response?.data?.message;
      notifyError(message || "Failed to delete staff.");
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    addStaff,
    updateStaff,
    deleteStaff,
    isProcessing,
  };
};
